import React, { FC, useEffect, useMemo } from 'react';
import { useGetActivityLogQuery, useGetDownloadLogQuery } from '../../api/projectsApi';
import { DownloadLogT } from '../../models/models';
import LogRow from '../LogRow/LogRow';
import { P } from '../Text/Text';
import Tabs from '../Tabs/Tabs';
import ActivityLogRow from '../ActivityLogRow/ActivityLogRow';
import { usePagination } from '../../utils/hooks/usePagination';
import Pagination from '../Pagination/Pagination';
import { DEFAULT_PAGE_ITEMS_COUNT } from '../../utils/constants';

import styles from './projectDataLogs.module.scss';

type ProjectDataLogsPropTypes = {
    projectId: string;
    pollingInterval: number | false;
    selectedTab: number;
    setSelectedTab: (value: number) => void;
    shareToken: string;
    // spec-41 D1/R3 — defaults to true, so every existing caller keeps both tabs. Delivery-service
    // users get the Download Log ONLY: they are the customer who paid for the delivery, not a
    // participant in the production flow the activity log narrates.
    showActivityLog?: boolean;
};

const tabs = ['Download Log', 'Activity Log'];
const DOWNLOAD_LOG_TAB = 0;

const ProjectDataLogs: FC<ProjectDataLogsPropTypes> = (props) => {
    const { projectId, pollingInterval, selectedTab, setSelectedTab, shareToken, showActivityLog = true } = props;

    // 🔴 spec-41 §4.3 — do NOT renumber `tabs`; slicing keeps Download Log at index 0, which is what
    // every index test below (and the caller's `selectedTab` state) is written against.
    const visibleTabs = showActivityLog ? tabs : tabs.slice(0, 1);
    // Belt and braces: the Activity tab is unclickable when hidden, but a caller's `selectedTab`
    // state is shared with other panels and could already be 1 — that would render an empty pane
    // with the pagination wired to a count that is never fetched.
    const activeTab = showActivityLog ? selectedTab : DOWNLOAD_LOG_TAB;

    const { prev, next, pages, page, setPage, setTotalCount } = usePagination();
    const { data: { Entries: downloadLog, ItemCount: DownloadLogItemCount } = [], isFetching: isLoadingDownloadLog } = useGetDownloadLogQuery({
        projectId,
        itemsPerPage: DEFAULT_PAGE_ITEMS_COUNT,
        pageNumber: page - 1,
        shareToken,
    }, {
        pollingInterval: pollingInterval as number,
        skip: !projectId,
    });
    const { data: { Entries: activityLog, ItemCount: ActivityLogItemCount } = [], isFetching: isLoadingActivityLog } = useGetActivityLogQuery({
        projectId,
        itemsPerPage: DEFAULT_PAGE_ITEMS_COUNT,
        pageNumber: page - 1,
        shareToken,
    }, {
        pollingInterval: pollingInterval as number,
        // spec-41 — no tab can reach this data, so do not keep polling for it every 10s.
        skip: !projectId || !showActivityLog,
    });
    const isLoading = useMemo(() => activeTab === 0 ? isLoadingDownloadLog : isLoadingActivityLog, [isLoadingDownloadLog, isLoadingActivityLog, activeTab]);

    useEffect(() => {
        // set total count based on shown log
        const itemCount = activeTab === 0 ? DownloadLogItemCount : ActivityLogItemCount;
        setTotalCount(itemCount || 0);
    }, [DownloadLogItemCount, ActivityLogItemCount, activeTab]);

    useEffect(() => {
        setPage(1);
    }, [activeTab]);

    return (
        <>
            <div className={styles.logTabWrapper}>
                <Tabs setSelectedTab={setSelectedTab} selectedTab={activeTab} tabs={visibleTabs} />
            </div>
            <div className={isLoading ? styles.loadingWrapper : styles.logWrapper}>
                {activeTab === 0 && (
                    downloadLog?.length ? downloadLog.map((item: DownloadLogT, index: number) => (
                        <LogRow item={item} key={index} />
                    )) : (
                        <P className={styles.noLogItemsText}>
                            The files for this project haven't been downloaded yet.
                        </P>
                    )
                )}
                {activeTab === 1 && (
                    activityLog?.length ? activityLog.map((item: any, index: number) => (
                        <ActivityLogRow item={item} key={index} />
                    )) : (
                        <P className={styles.noLogItemsText}>
                            No activity yet.
                        </P>
                    )
                )}
            </div>
            <div className={styles.paginationWrapper}>
                <Pagination prev={prev} next={next} pages={pages} page={page} setPage={setPage} simplified />
            </div>
        </>
    );
};

export default ProjectDataLogs;